import { useSessionContext } from 'src/context/session-context'

export type CatalogAccess = {
  /**
   * May a restricted action be attempted? The server predicate verbatim, **fail-open**
   * included: an unresolvable subscription lookup reads as `true` here, deliberately, so a
   * RevenueCat blip never paywalls a paying customer.
   *
   * Use this for gating an attempt. Do NOT use it to hide anything.
   */
  hasFullCatalogAccess: boolean
  /**
   * Is the account **definitely** entitled? `hasFullCatalogAccess` AND the server actually
   * resolved it (`catalogAccessResolved`).
   *
   * Use this wherever being wrong means REMOVING something. The featured shelf renders when
   * the account is *not* entitled, so on that axis the fail-open value inverts: a lookup blip
   * would hide the free shelf from an account that is not entitled, while the free workouts it
   * advertises stay playable — the server grants those on `free` alone. So an outage would take
   * working content away from exactly the audience the shelf exists for.
   */
  isDefinitelyEntitled: boolean
  /**
   * The config has not answered yet. A third state, not "no access".
   *
   * `hustlerConfig` starts on a fail-closed default, so a surface that hides on
   * `isDefinitelyEntitled` alone renders for the whole of the first fetch and then vanishes.
   * That flash is what this exists to prevent — a subscriber briefly seeing a shelf of free
   * workouts, skeletons and all, before it disappears.
   */
  isLoading: boolean
}

/**
 * The client half of the server's access predicate (GH-866), read off
 * `GET /hustlers/me/config` via `SessionContext`.
 *
 * **Two answers, not one.** `hasFullCatalogAccess` and `isDefinitelyEntitled` differ only when
 * the server's lookup failed, and which one a call site wants depends on which way being wrong
 * hurts: gates fail open (a false negative paywalls a subscriber), and anything that hides a
 * surface requires certainty (a false positive removes working content). One boolean could not
 * serve both — mobile has the identical split in `hooks/use-catalog-access.ts`.
 *
 * Do not derive an equivalent from subscription state locally; that duplication in two
 * languages is the problem the server predicate exists to remove.
 *
 * It reads from `SessionContext`, so it is only usable inside `CoreLayout`. That is the right
 * scope: its consumers are the Home shelf, the play gate and the upsell, all authed surfaces.
 * The public login-page shelf carries no account state at all and must never call this.
 *
 * `SessionContext.hasActiveSubscription` is deliberately left in place for the membership and
 * profile surfaces; migrating those is not this change.
 */
export const useCatalogAccess = (): CatalogAccess => {
  const { hustlerConfig, isHustlerConfigLoading } = useSessionContext()

  // `hustlerConfig` is a `useState` default in `SessionContextProvider` and should never
  // actually be absent, but this hook is now called from `AppTour`/`MainNav` on every
  // authenticated route (not just Home), and under React 18 concurrent rendering a
  // component can render against a momentarily-stale context during an interrupted render
  // pass. Optional-chain to the same fail-closed default `SessionContext` itself uses,
  // rather than let a transient render glitch throw an app-wide error boundary.
  const hasFullCatalogAccess = hustlerConfig?.hasFullCatalogAccess ?? false
  // Absent (a backend older than GH-866) counts as resolved, which is exactly how the app
  // behaved before the field existed. Treating absent as unresolved would permanently show
  // the shelf to subscribers on any older deployment.
  const resolved = hustlerConfig?.catalogAccessResolved ?? true

  return {
    hasFullCatalogAccess,
    isDefinitelyEntitled: hasFullCatalogAccess && resolved,
    isLoading: isHustlerConfigLoading,
  }
}
